import { difference, union } from "lodash";
import {
  type Identifier,
  RecordContextProvider,
  useListContext,
  useTranslate,
} from "ra-core";
import { type MouseEvent, useCallback, useRef } from "react";
import { Link } from "react-router";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Building2,
  Users,
  Handshake,
  MapPin,
  Globe,
  Phone,
  ChevronRight,
} from "lucide-react";

import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Company } from "../types";
import { CompanyAvatar } from "./CompanyAvatar";
import { getTranslatedCompanySizeLabel } from "./getTranslatedCompanySizeLabel";
import { sizes } from "./sizes";
import { cn } from "@/lib/utils";

export const CompanyListContent = () => {
  const translate = useTranslate();
  const {
    data: companies,
    error,
    isPending,
    onToggleItem,
    onSelect,
    selectedIds = [],
  } = useListContext<Company>();
  const lastSelected = useRef<Identifier | null>(null);

  // Seleção múltipla com Shift+Click
  const handleToggleItem = useCallback(
    (id: Identifier, event: MouseEvent) => {
      if (!companies) return;

      const ids = companies.map((c) => c.id);
      const lastSelectedIndex = lastSelected.current
        ? ids.indexOf(lastSelected.current)
        : -1;

      if (event.shiftKey && lastSelectedIndex !== -1) {
        const index = ids.indexOf(id);
        const idsBetweenSelections = ids.slice(
          Math.min(lastSelectedIndex, index),
          Math.max(lastSelectedIndex, index) + 1,
        );

        const isClickedItemSelected = selectedIds?.includes(id);
        const newSelectedIds = isClickedItemSelected
          ? difference(selectedIds, idsBetweenSelections)
          : union(selectedIds, idsBetweenSelections);

        onSelect?.(newSelectedIds);
      } else {
        onToggleItem(id);
      }

      lastSelected.current = id;
    },
    [companies, selectedIds, onSelect, onToggleItem],
  );

  if (isPending) {
    return (
      <div className="p-4 space-y-3">
        <Skeleton className="w-full h-12 rounded-xl" />
        <Skeleton className="w-full h-12 rounded-xl" />
        <Skeleton className="w-full h-12 rounded-xl" />
      </div>
    );
  }

  if (error) {
    return null;
  }

  if (!companies || companies.length === 0) {
    return (
      <div className="p-8 text-center text-muted-foreground text-sm">
        {translate("resources.companies.empty.title", {
          _: "Nenhuma organização encontrada",
        })}
      </div>
    );
  }

  return (
    <div className="divide-y divide-border/60">
      {companies.map((company) => (
        <RecordContextProvider key={company.id} value={company}>
          <CompanyItemContent
            company={company}
            handleToggleItem={handleToggleItem}
          />
        </RecordContextProvider>
      ))}
    </div>
  );
};

const CompanyItemContent = ({
  company,
  handleToggleItem,
}: {
  company: Company;
  handleToggleItem: (id: Identifier, event: MouseEvent) => void;
}) => {
  const translate = useTranslate();
  const { selectedIds = [] } = useListContext<Company>();
  const { companySectors } = useConfigurationContext();

  const sector = companySectors.find((s) => s.value === company.sector);
  const sectorLabel = sector?.label || company.sector;

  const sizeObj = sizes.find((s) => s.id === company.size);
  const sizeLabel = sizeObj
    ? getTranslatedCompanySizeLabel(sizeObj, translate)
    : null;

  const isSelected = selectedIds.includes(company.id);

  return (
    <div
      className={cn(
        "flex flex-row items-center pl-2 pr-4 py-3 hover:bg-muted/70 transition-colors first:rounded-t-xl last:rounded-b-xl group",
        isSelected && "bg-muted/40",
      )}
    >
      {/* Checkbox de seleção */}
      <div
        className="px-3 py-2 flex items-center cursor-pointer"
        onClick={(e) => handleToggleItem(company.id, e)}
      >
        <Checkbox className="cursor-pointer" checked={isSelected} />
      </div>

      {/* Linha inteira clicável que leva aos Detalhes da Organização */}
      <Link
        to={`/companies/${company.id}/show`}
        className="flex-1 flex flex-col md:flex-row md:items-center justify-between gap-4 min-w-0"
      >
        {/* Lado Esquerdo: Avatar + Nome + Sub-informações */}
        <div className="flex items-center gap-3.5 min-w-0 flex-1">
          <CompanyAvatar width={40} record={company} />

          <div className="flex-1 min-w-0">
            {/* Linha Principal: Nome da Organização + Badges */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors truncate">
                {company.name}
              </span>

              {sectorLabel && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium bg-primary/10 text-primary border border-primary/20">
                  {sectorLabel}
                </span>
              )}

              {company.tax_identifier && (
                <span className="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono text-muted-foreground bg-muted border border-border/60">
                  CNPJ: {company.tax_identifier}
                </span>
              )}
            </div>

            {/* Linha Secundária: Porte, Cidade/UF, Telefone, Website */}
            <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
              {sizeLabel && (
                <span className="flex items-center gap-1">
                  <Building2 className="w-3 h-3 text-muted-foreground/70 shrink-0" />
                  <span>{sizeLabel}</span>
                </span>
              )}

              {(company.city || company.state_abbr) && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-muted-foreground/70 shrink-0" />
                  <span>
                    {[company.city, company.state_abbr]
                      .filter(Boolean)
                      .join(", ")}
                  </span>
                </span>
              )}

              {company.phone_number && (
                <span className="hidden md:flex items-center gap-1">
                  <Phone className="w-3 h-3 text-muted-foreground/70 shrink-0" />
                  <span>{company.phone_number}</span>
                </span>
              )}

              {company.website && (
                <span className="hidden lg:flex items-center gap-1">
                  <Globe className="w-3 h-3 text-muted-foreground/70 shrink-0" />
                  <span className="truncate max-w-[180px]">
                    {company.website
                      .replace(/^https?:\/\//, "")
                      .replace(/\/$/, "")}
                  </span>
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Lado Direito: Contatos, Negócios e Ícone de Detalhes */}
        <div className="flex items-center gap-3 shrink-0 self-end md:self-center">
          {/* Contatos Vinculados */}
          <div
            className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium bg-muted/50 px-2.5 py-1 rounded-lg border border-border/50"
            title="Contatos vinculados"
          >
            <Users className="w-3.5 h-3.5 text-primary/70" />
            <span>
              {company.nb_contacts ?? 0}{" "}
              {company.nb_contacts === 1 ? "contato" : "contatos"}
            </span>
          </div>

          {/* Negócios Vinculados */}
          <div
            className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium bg-muted/50 px-2.5 py-1 rounded-lg border border-border/50"
            title="Negócios vinculados"
          >
            <Handshake className="w-3.5 h-3.5 text-emerald-600/80" />
            <span>
              {company.nb_deals ?? 0}{" "}
              {company.nb_deals === 1 ? "negócio" : "negócios"}
            </span>
          </div>

          {/* Seta indicando detalhes */}
          <ChevronRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-primary group-hover:translate-x-0.5 transition-all hidden sm:block" />
        </div>
      </Link>
    </div>
  );
};
